//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import { createSelector } from 'reselect';

import { ICuralateStore } from '@models/AppState';
import DraftId from '@models/DraftId';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import {
  PostDetailsHeaderText,
  VideoAutomaticPostDetailsFormFieldErrorText,
} from '@tiktok/components/schedule/post-details/tiktokPostDetailsConstants';
import { getThumbnailOffsetInSeconds } from '@tiktok/redux/schedule/post-details/tiktokPostDetailsUtils';
import { TikTokVideoAutomaticValidationError } from '@tiktok/redux/schedule/post-details/video-automatic/tiktokVideoAutomaticPostDetailsModel';
import { isDefined } from '@utils/filterPredicates';

const getVideoAutomaticPostDetails = (state: ICuralateStore) =>
  state.tiktok.schedule.postDetails.videoAutomatic;

const getVideoAutomaticCurrentPostStatus = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.status,
);

export const getVideoAutomaticCurrentPostHasFailureStatus = createSelector(
  getVideoAutomaticCurrentPostStatus,
  (status) => status === PostableStatus.Failed,
);

export const getVideoAutomaticIsFetchingCuid = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.flags.isFetchingCuid,
);

export const getVideoAutomaticPostableId = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.postableId,
);

export const getVideoAutomaticDraftId = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.draftId,
);

export const getVideoAutomaticCurrentCuid = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.cuid,
);

export const getVideoAutomaticCurrentCaption = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.caption,
);

export const getVideoAutomaticCurrentValidationErrors = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.validationErrors,
);

export const getVideoAutomaticCurrentScheduledTime = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.scheduledTime,
);

export const getVideoAutomaticCurrentDisableComment = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.disableComment,
);

export const getVideoAutomaticCurrentDisableDuet = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.disableDuet,
);

export const getVideoAutomaticCurrentDisableStitch = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.disableStitch,
);

export const getVideoAutomaticCurrentThumbnailOffsetInMs = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.currentPostDetails.thumbnailOffsetInMs,
);

export const getVideoAutomaticCurrentThumbnailOffsetInSeconds = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) =>
    getThumbnailOffsetInSeconds(
      postDetails.currentPostDetails.thumbnailOffsetInMs,
    ),
);

export const getVideoAutomaticIsSchedulingPost = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.flags.isSchedulingPost,
);

export const getVideoAutomaticIsTikTokThumbnailSelectionModalOpen =
  createSelector(
    getVideoAutomaticPostDetails,
    (postDetails) => postDetails.isTikTokThumbnailSelectionModalOpen,
  );

export const getVideoAutomaticIsSavingDraftPost = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.flags.isSavingDraftPost,
);

export const getVideoAutomaticIsEditingPost = createSelector(
  getVideoAutomaticPostableId,
  (postableId) => isDefined(postableId),
);

export const getVideoAutomaticIsEditingDraft = createSelector(
  getVideoAutomaticDraftId,
  (draftId) => isDefined(draftId),
);

export const getVideoAutomaticScheduleEntryPoint = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.schedulingEntryPoint,
);

export const getVideoAutomaticInfoModalDetails = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.postDetailsInfoModal,
);

export const getVideoAutomaticHasSetInitialPostDetails = createSelector(
  getVideoAutomaticPostDetails,
  (postDetails) => postDetails.flags.hasSetInitialPostDetails,
);

const getPostDetailsHeaderTextImpl = (
  postableId: PostableId | undefined,
  draftId: DraftId | undefined,
) => {
  if (isDefined(draftId)) {
    return PostDetailsHeaderText.ReviewDraft;
  }

  return isDefined(postableId)
    ? PostDetailsHeaderText.ReviewVideoPost
    : PostDetailsHeaderText.CreateVideoPost;
};

export const getPostDetailsHeaderText = createSelector(
  getVideoAutomaticPostableId,
  getVideoAutomaticDraftId,
  getPostDetailsHeaderTextImpl,
);

const getVideoMediaSectionErrorTextImpl = (
  validationErrors: PojoSet<TikTokVideoAutomaticValidationError>,
) => {
  const noMediaErrorText =
    validationErrors[TikTokVideoAutomaticValidationError.NoMedia] &&
    VideoAutomaticPostDetailsFormFieldErrorText.NoMedia;

  const invalidVideoDurationText =
    validationErrors[
      TikTokVideoAutomaticValidationError.InvalidVideoDuration
    ] && VideoAutomaticPostDetailsFormFieldErrorText.InvalidVideoDuration;

  const invalidVideoDimensionsText =
    validationErrors[
      TikTokVideoAutomaticValidationError.InvalidVideoDimensions
    ] && VideoAutomaticPostDetailsFormFieldErrorText.InvalidVideoDimensions;

  const invalidThumbnailOffsetText =
    validationErrors[
      TikTokVideoAutomaticValidationError.InvalidThumbnailOffset
    ] && VideoAutomaticPostDetailsFormFieldErrorText.InvalidThumbnailOffset;

  return (
    noMediaErrorText ||
    invalidVideoDurationText ||
    invalidVideoDimensionsText ||
    invalidThumbnailOffsetText
  );
};

export const getVideoAutomaticMediaSectionErrorText = createSelector(
  getVideoAutomaticCurrentValidationErrors,
  getVideoMediaSectionErrorTextImpl,
);
